import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tos-backdrop"
}
const _hoisted_2 = {
  class: "tos-sheet",
  role: "dialog",
  "aria-modal": "true",
  "aria-label": "Duelly Terms of Use"
}
const _hoisted_3 = { class: "tos-body" }
const _hoisted_4 = { class: "tos-scroll" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "agree-row" }
const _hoisted_7 = { class: "tos-footer" }
const _hoisted_8 = ["disabled"]

import { computed, ref } from 'vue';

interface Props {
    modelValue: boolean; // controls visibility
    termsHtml?: string; // optional pre-rendered terms HTML (overrides default)
}
const DEFAULT_TERMS_HTML = `
  <article class="terms">
    <h1>Duelly Terms &amp; Conditions</h1>
    <p><strong>Effective Date:</strong> January 1, 2025</p>
    <p>Welcome to Duelly, Inc. (“Duelly,” “we,” “our,” “us”). These Terms of Use (“Terms”) govern your use of the Duelly mobile application, web platform, and related services (collectively, the “Services”). By creating an account or using our Services, you agree to these Terms. If you do not agree, do not use Duelly.</p>

    <h2>1. Eligibility</h2>
    <ul>
      <li>You must be at least 13 years old to use Duelly. If you are under 18, you may only use Duelly with the consent and supervision of a parent or guardian.</li>
      <li>You must comply with all applicable laws when using Duelly.</li>
      <li>We may restrict or terminate access if we reasonably believe you are not eligible or are misusing the Services.</li>
    </ul>

    <h2>2. Your Account</h2>
    <ul>
      <li>You must provide accurate, current, and complete information when registering.</li>
      <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</li>
      <li>You may not impersonate another person, misrepresent your identity, or create an account for someone else without permission.</li>
      <li>We reserve the right to suspend or terminate accounts at our sole discretion.</li>
    </ul>

    <h2>3. User Content &amp; License</h2>
    <ul>
      <li>You retain ownership of the videos, photos, posts, comments, and other content you create (“User Content”).</li>
      <li>By submitting User Content, you grant Duelly a non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to host, use, distribute, modify, display, reproduce, and create derivative works for the purposes of operating, promoting, and improving Duelly.</li>
      <li>This license continues even if you delete your content, subject to reasonable retention (e.g., backups, legal obligations, investigations).</li>
      <li>You represent that you own or have rights to all User Content you post and that it does not infringe or violate the rights of others.</li>
    </ul>

    <h2>4. Community Standards</h2>
    <p>You agree not to post or share:</p>
    <ul>
      <li>Nudity or sexually explicit content.</li>
      <li>Hate speech, harassment, threats, or discriminatory content.</li>
      <li>Violence, self-harm, or dangerous activity.</li>
      <li>False information, spam, scams, or fraudulent schemes.</li>
      <li>Content that violates law or intellectual property rights.</li>
    </ul>
    <p>Duelly may remove content or restrict accounts that violate these standards.</p>

    <h2>5. Challenges, Rewards &amp; Promotions</h2>
    <ul>
      <li>Certain challenges may offer rewards or prizes. Eligibility and compliance will be verified before awarding.</li>
      <li>Taxes, fees, or legal obligations related to rewards are the responsibility of the user.</li>
      <li>By participating in challenges, you grant Duelly the right to use your User Content for promotional purposes, including showcasing submissions in marketing, advertising, and social channels.</li>
    </ul>

    <h2>6. Intellectual Property</h2>
    <ul>
      <li>Duelly owns the Services, including the platform, trademarks, branding, and technology.</li>
      <li>Except as expressly permitted, you may not copy, modify, distribute, reverse-engineer, or exploit any part of the Services.</li>
    </ul>

    <h2>7. Advertising &amp; Sponsored Content</h2>
    <ul>
      <li>Duelly may display ads, sponsored content, or promotions alongside User Content.</li>
      <li>By using Duelly, you agree that Duelly may use your User Content in connection with ads and promotions without additional compensation.</li>
      <li>If you post branded or sponsored content, you must comply with applicable laws and disclose partnerships.</li>
    </ul>

    <h2>8. Third-Party Services</h2>
    <p>Duelly may contain links or integrations to third-party services. We are not responsible for third-party content, terms, or policies.</p>

    <h2>9. Data &amp; Privacy</h2>
    <p>Use of Duelly is also governed by our Privacy Policy, which explains how we collect, use, and share information.</p>

    <h2>10. Disclaimers</h2>
    <p>Duelly is provided on an “as is” and “as available” basis without warranties of any kind. We do not guarantee uninterrupted service, error-free operation, or accuracy of content.</p>

    <h2>11. Limitation of Liability</h2>
    <p>To the fullest extent permitted by law, Duelly will not be liable for indirect, incidental, or consequential damages. Our total liability for any claims arising from use of the Services shall not exceed the greater of (a) $100 or (b) the amount you paid us in the past 12 months.</p>

    <h2>12. Indemnification</h2>
    <p>You agree to indemnify and hold harmless Duelly, its affiliates, employees, and partners from claims, damages, losses, or expenses arising from: (i) your use of the Services, (ii) your violation of these Terms, or (iii) your infringement of others’ rights.</p>

    <h2>13. Termination</h2>
    <ul>
      <li>You may delete your account at any time.</li>
      <li>We may suspend or permanently terminate your account without notice if you violate these Terms or use the Services in harmful ways.</li>
    </ul>

    <h2>14. Changes to Terms</h2>
    <ul>
      <li>We may update these Terms from time to time.</li>
      <li>We will notify you of material changes (e.g., via app notice or email).</li>
      <li>Continued use of the Services after changes means you accept the updated Terms.</li>
    </ul>

    <h2>15. Governing Law &amp; Disputes</h2>
    <p>These Terms are governed by the laws of the State of Florida, United States, without regard to conflict-of-law principles. Disputes may be resolved in the state or federal courts located in Florida, unless arbitration is required by law. You waive the right to participate in class actions against Duelly.</p>

    <h2>16. Severability</h2>
    <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force.</p>

    <h2>17. Contact Us</h2>
    <address>
      Duelly, Inc.<br />
      1506 SE 16th Street<br />
      Cape Coral, FL 33990<br />
      Email: <a href="mailto:contact@duelly.net">contact@duelly.net</a>
    </address>
  </article>
`;

export default /*@__PURE__*/_defineComponent({
  __name: 'TermsOfUseModal',
  props: {
    modelValue: { type: Boolean },
    termsHtml: {}
  },
  emits: ["update:modelValue", "accepted", "declined"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const visible = computed({
    get: () => props.modelValue,
    set: (v: boolean) => emit('update:modelValue', v),
});
const agreed = ref(false);

function onAccept() {
    if (!agreed.value) return;
    emit('accepted');
}
function onDecline() {
    emit('declined');
}

/** If you pass `termsHtml`, it will override this default. */

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "fade" }, {
    default: _withCtx(() => [
      (visible.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _cache[2] || (_cache[2] = _createElementVNode("header", { class: "tos-header" }, [
                _createElementVNode("h2", null, "Terms of Use"),
                _createElementVNode("p", { class: "sub" }, "Effective Date: January 1, 2025")
              ], -1)),
              _createElementVNode("section", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", {
                    class: "tos-html",
                    innerHTML: _ctx.termsHtml || DEFAULT_TERMS_HTML
                  }, null, 8, _hoisted_5)
                ]),
                _createElementVNode("label", _hoisted_6, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((agreed).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, agreed.value]
                  ]),
                  _cache[1] || (_cache[1] = _createElementVNode("span", null, "I have read and agree to the Duelly Terms of Use.", -1))
                ])
              ]),
              _createElementVNode("footer", _hoisted_7, [
                _createElementVNode("button", {
                  class: "btn ghost",
                  onClick: onDecline
                }, "Decline"),
                _createElementVNode("button", {
                  class: "btn primary",
                  disabled: !agreed.value,
                  onClick: onAccept
                }, " Accept ", 8, _hoisted_8)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})